// app/javascript/components/Notifications/NotificationPanel.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Divider, Button, Collapse, Link as MuiLink } from "@mui/material";
import { getUserNotificationsPage, markNotificationSeen } from "../../api/notifications";
import { isDarkMode } from "../../utils/constants";
import MessageSegments from "../Chat/MessageSegments";
import {
  agentTaskNotificationContent,
  agentTaskNotificationTextColor,
} from "./notificationContent";

function AgentTaskNotificationContent({ notification }) {
  const content = agentTaskNotificationContent(notification);
  if (!content) return null;

  return (
    <Box sx={{ color: agentTaskNotificationTextColor(isDarkMode) }}>
      <MessageSegments
        msgId={`user-notification-${notification?.id || "unknown"}`}
        raw={content}
        isDark={isDarkMode}
        isStreaming={false}
      />
    </Box>
  );
}

export default function NotificationPanel({ onUnreadCountChange = () => {} }) {
  const [items, setItems] = useState([]);
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const itemsRef = useRef([]);

  const PAGE = 30;

  const load = async ({ reset = false } = {}) => {
    if (loading) return;
    setLoading(true);

    const nextOffset = reset ? 0 : offset;
    const page = await getUserNotificationsPage({ limit: PAGE, offset: nextOffset });

    if (page?.error) {
      setLoading(false);
      return;
    }

    const arr = Array.isArray(page) ? page : [];
    setItems((prev) => (reset ? arr : [...prev, ...arr]));
    setOffset(nextOffset + arr.length);
    setHasMore(arr.length === PAGE);
    setLoading(false);
  };

  useEffect(() => {
    load({ reset: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    itemsRef.current = Array.isArray(items) ? items : [];
  }, [items]);

  useEffect(() => {
    const handler = (event) => {
      const nextNotification = event?.detail?.notification;
      if (!nextNotification?.id) return;

      const currentItems = Array.isArray(itemsRef.current) ? itemsRef.current : [];
      const existingIndex = currentItems.findIndex(
        (item) => String(item.id) === String(nextNotification.id)
      );

      if (existingIndex >= 0) {
        const nextItems = currentItems.map((item) =>
          String(item.id) === String(nextNotification.id)
            ? { ...item, ...nextNotification }
            : item
        );
        itemsRef.current = nextItems;
        setItems(nextItems);
        return;
      }

      const nextItems = [nextNotification, ...currentItems];
      itemsRef.current = nextItems;
      setItems(nextItems);
      setOffset((prev) => prev + 1);
    };

    window.addEventListener("user-notifications:updated", handler);
    return () => window.removeEventListener("user-notifications:updated", handler);
  }, []);

  // ----- datetime formatting rules you specified
  const fmtWhen = (n) => {
    const iso = n?.createdAt || n?.created_at || n?.updatedAt || n?.updated_at;
    if (!iso) return "";

    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";

    const now = new Date();

    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const startOfYesterday = new Date(startOfToday);
    startOfYesterday.setDate(startOfYesterday.getDate() - 1);

    const startOfDayD = new Date(d.getFullYear(), d.getMonth(), d.getDate());

    // Today => time only
    if (startOfDayD.getTime() === startOfToday.getTime()) {
      return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
    }

    // Yesterday => literal "Yesterday"
    if (startOfDayD.getTime() === startOfYesterday.getTime()) {
      return "Yesterday";
    }

    // Within last 7 days => weekday
    const msSinceStartOfD = startOfToday.getTime() - startOfDayD.getTime();
    const daysAgo = Math.floor(msSinceStartOfD / (24 * 60 * 60 * 1000));
    if (daysAgo >= 2 && daysAgo <= 6) {
      return d.toLocaleDateString(undefined, { weekday: "short" }); // Mon, Tue, ...
    }

    // Older => full date/time (your existing style)
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // ----- rich text renderer: supports [label](url) + bare URLs + emails, preserves newlines
  const MD_LINK_RE = useMemo(
    () => /\[([^\]]+)\]\((https?:\/\/[^\s)]+|www\.[^\s)]+)\)/gi,
    []
  );
  const EMAIL_RE = useMemo(
    () => /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi,
    []
  );
  const URL_OR_EMAIL_RE = useMemo(
    () => /(\bhttps?:\/\/[^\s]+|\bwww\.[^\s]+|\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b)/gi,
    []
  );

  function splitTrailingPunct(token) {
    // E.g. "andrew@x.com)." => "andrew@x.com" + ")."
    const m = token.match(/^(.*?)([)\].,!?;:]+)$/);
    return m ? { core: m[1], tail: m[2] } : { core: token, tail: "" };
  }

  const linkSx = {
    color: isDarkMode ? "#60a5fa" : "#2563eb",
    textDecoration: "underline",
    fontWeight: 800,
    wordBreak: "break-word",
    display: "inline-block",
    px: "3px",
    py: "1px",
    borderRadius: "6px",
    cursor: "pointer",
    "&:hover": {
      textDecoration: "underline",
      bgcolor: isDarkMode ? "rgba(96,165,250,0.12)" : "rgba(37,99,235,0.10)",
    },
  };

  const renderRichText = (text) => {
    const s = (text ?? "").toString();
    if (!s) return null;

    // 1) turn markdown links into tokens
    const chunks = [];
    let last = 0;
    let m;
    while ((m = MD_LINK_RE.exec(s)) !== null) {
      const [whole, label, urlRaw] = m;
      const start = m.index;
      if (start > last) chunks.push({ type: "text", value: s.slice(last, start) });

      const href = urlRaw.startsWith("http") ? urlRaw : `https://${urlRaw}`;
      chunks.push({ type: "link", label, href, key: `mdl-${start}` });

      last = start + whole.length;
    }
    if (last < s.length) chunks.push({ type: "text", value: s.slice(last) });

    // 2) within text chunks, linkify URLs + emails
    const nodes = [];
    chunks.forEach((c, i) => {
      if (c.type === "link") {
        nodes.push(
          <MuiLink
            key={c.key}
            href={c.href}
            target="_blank"
            rel="noreferrer"
            onClick={(e) => e.stopPropagation()}
            sx={linkSx}
          >
            {c.label}
          </MuiLink>
        );
        return;
      }

      const t = c.value;
      let lastIdx = 0;
      let mm;

      URL_OR_EMAIL_RE.lastIndex = 0;
      while ((mm = URL_OR_EMAIL_RE.exec(t)) !== null) {
        const raw = mm[0];
        const start = mm.index;

        if (start > lastIdx) nodes.push(t.slice(lastIdx, start));

        const { core, tail } = splitTrailingPunct(raw);

        const isEmail = EMAIL_RE.test(core);
        EMAIL_RE.lastIndex = 0;

        let href = core;
        if (isEmail) {
          href = `mailto:${core}`;
        } else {
          href = core.startsWith("http") ? core : `https://${core}`;
        }

        nodes.push(
          <MuiLink
            key={`tok-${i}-${start}`}
            href={href}
            target={isEmail ? undefined : "_blank"}
            rel={isEmail ? undefined : "noreferrer"}
            onClick={(e) => e.stopPropagation()}
            sx={linkSx}
          >
            {core}
          </MuiLink>
        );

        if (tail) nodes.push(tail);

        lastIdx = start + raw.length;
      }

      if (lastIdx < t.length) nodes.push(t.slice(lastIdx));
    });

    return (
      <Typography
        sx={{
          fontSize: 14,
          whiteSpace: "pre-wrap",
          color: isDarkMode ? "#e5e7eb" : "#111827",
        }}
      >
        {nodes}
      </Typography>
    );
  };

  const normalizedActionUrl = (actionUrl) => {
    const raw = (actionUrl || "").toString().trim();
    if (!raw) return "";
    if (/^(https?:\/\/|mailto:|tel:|sms:)/i.test(raw)) return raw;
    return raw.startsWith("/") ? raw : `/${raw.replace(/^\/+/, "")}`;
  };

  const markSeenIfNeeded = async (n) => {
    if (n.seenAt) return;

    const nowIso = new Date().toISOString();

    // optimistic
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, seenAt: nowIso } : x)));

    try {
      await markNotificationSeen(n.id);
      onUnreadCountChange();
    } catch {
      // revert on failure (optional)
      setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, seenAt: null } : x)));
    }
  };

  const onToggle = async (n) => {
    const willOpen = expandedId !== n.id;
    setExpandedId(willOpen ? n.id : null);
    if (willOpen) await markSeenIfNeeded(n);
  };

  const ScrollableMessageBody = ({ children, maxHeight = 400, fadeBg }) => {
    const scrollRef = useRef(null);
    const [canScrollDown, setCanScrollDown] = useState(false);

    const updateScrollState = () => {
      const el = scrollRef.current;
      if (!el) return;
      const maxScroll = el.scrollHeight - el.clientHeight;
      const scrollTop = el.scrollTop;
      setCanScrollDown(maxScroll > 1 && scrollTop < maxScroll - 1);
    };

    useEffect(() => {
      updateScrollState();
    }, [children]);

    return (
      <Box sx={{ position: "relative" }}>
        <Box
          ref={scrollRef}
          onScroll={updateScrollState}
          sx={{
            maxHeight,
            overflowY: "auto",
            pr: 0.5,
            pb: 1,
          }}
        >
          {children}
        </Box>

        {canScrollDown && (
          <Box
            sx={{
              pointerEvents: "none",
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: 28,
              background: `linear-gradient(180deg, rgba(0,0,0,0) 0%, ${fadeBg} 80%)`,
              display: "flex",
              alignItems: "flex-end",
              justifyContent: "center",
              pb: 0.25,
            }}
          >
            <Typography sx={{ fontSize: 14, color: isDarkMode ? "#9ca3af" : "#6b7280" }}>
              ▾
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ px: 2, py: 1.5, height: "100%", display: "flex", flexDirection: "column", minHeight: 0 }}>
      <Typography sx={{ fontSize: 18, fontWeight: 700, mb: 1, color: isDarkMode ? "#fff" : "#111" }}>
        Inbox
      </Typography>

      <Divider sx={{ mb: 1.5, borderColor: isDarkMode ? "#2f2f2f" : "#e7e7e7" }} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", pr: 0.5 }}>
        {items.map((n) => {
          const isExpanded = expandedId === n.id;
          const isUnread = !n.seenAt;
          const agentTaskContent = agentTaskNotificationContent(n);
          const cardBg = isUnread ? (isDarkMode ? "#111827" : "#EBEEF3") : (isDarkMode ? "#121212" : "#FFFFFF");

          return (
            <Box key={n.id} sx={{ mb: 0.75 }}>
              {/* Non-clickable card container */}
              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: isDarkMode ? "#2f2f2f" : "#e7e7e7",
                  bgcolor: cardBg,
                  "&:hover": {
                    bgcolor: isUnread
                      ? (isDarkMode ? "#0f172a" : "#EEF6FF")
                      : (isDarkMode ? "#161616" : "#FAFAFA"),
                  },
                }}
              >
                {/* header row: ONLY thing that toggles */}
                <Box
                  role="button"
                  tabIndex={0}
                  onClick={() => onToggle(n)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onToggle(n);
                    }
                  }}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    justifyContent: "space-between",
                    gap: 1,
                    cursor: "pointer",
                    userSelect: "none",
                  }}
                >
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: 15, fontWeight: 700, color: isDarkMode ? "#fff" : "#111" }} noWrap>
                      {n.title || "Notification"}
                    </Typography>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
                    <Typography sx={{ fontSize: 14, color: isDarkMode ? "#94a3b8" : "#64748b", whiteSpace: "nowrap" }}>
                      {fmtWhen(n)}
                    </Typography>

                    {isUnread && (
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: 999,
                          flexShrink: 0,
                          bgcolor: "#3B82F6",
                          boxShadow: isDarkMode
                            ? "0 0 0 2px rgba(59,130,246,0.25)"
                            : "0 0 0 2px rgba(59,130,246,0.15)",
                        }}
                      />
                    )}
                  </Box>
                </Box>

              {/* expanded content only (selectable, not clickable for collapse) */}
              <Collapse in={isExpanded} timeout={200}>
                {(n.text || n.body || n.actionUrl) && (
                  <Box sx={{ mt: 1.6, cursor: "text", userSelect: "text" }}>
                    {(n.text || n.body) && (
                      <ScrollableMessageBody fadeBg={cardBg}>
                        {agentTaskContent ? (
                          <AgentTaskNotificationContent notification={n} />
                        ) : (
                          <>
                            {n.text && renderRichText(n.text)}
                            {n.body && (
                              <Box sx={{ mt: n.text ? 1 : 0 }}>
                                {renderRichText(n.body)}
                              </Box>
                            )}
                          </>
                        )}
                      </ScrollableMessageBody>
                    )}

                    {n.actionUrl && (
                      <Box sx={{ mt: 1.25 }}>
                        <Button
                          component="a"
                          href={normalizedActionUrl(n.actionUrl)}
                          target={
                            /^https?:\/\//i.test(normalizedActionUrl(n.actionUrl))
                              ? "_blank"
                              : undefined
                          }
                          rel={
                            /^https?:\/\//i.test(normalizedActionUrl(n.actionUrl))
                              ? "noreferrer"
                              : undefined
                          }
                          variant="contained"
                          size="small"
                          sx={{ textTransform: "none", borderRadius: "999px" }}
                        >
                          Open
                        </Button>
                      </Box>
                    )}
                  </Box>
                )}
              </Collapse>
              </Box>
            </Box>
          );
        })}

        {hasMore && (
          <Button
            onClick={() => load({ reset: false })}
            disabled={loading}
            sx={{ textTransform: "none", borderRadius: 999 }}
            variant="contained"
            fullWidth
          >
            {loading ? "Loading..." : "Load more"}
          </Button>
        )}
      </Box>
    </Box>
  );
}
